import { z } from "zod";
import type { BudgetRow } from "./mjop.js";

/**
 * Reservering voor het MJOP (docs/ASSETS-MJOP.md, "Reservering"): per gebouw een fonds met
 * startsaldo, storting, rente en de vervangingen uit het MJOP als onttrekking. Alleen vervangingen
 * (investeringen): onderhoud, inspectie en herstel zijn operationele kosten en tellen niet mee.
 * Alles in centen, exclusief btw.
 */
export const RESERVE_METHODS = ["cost_covering", "cost_covering_indexed", "components"] as const;
export type ReserveMethod = (typeof RESERVE_METHODS)[number];
export const RESERVE_METHOD_LABELS: Record<ReserveMethod, string> = {
  cost_covering: "Kostendekkend, vast bedrag",
  cost_covering_indexed: "Kostendekkend, meegroeiend met de index",
  components: "Componentenmethode",
};
export const RESERVE_METHOD_HINTS: Record<ReserveMethod, string> = {
  cost_covering: "Het laagste vaste bedrag per jaar waarbij het fonds in geen enkel jaar tekortkomt.",
  cost_covering_indexed:
    "Als kostendekkend, maar de storting stijgt elk jaar met de index: lager beginnen, later meer.",
  components:
    "Elke vervanging spaart apart: wat al versleten is, ligt klaar; de rest wordt tot de vervanging bijgelegd.",
};
/** Wettelijke ondergrens voor een VvE zonder MJOP (art. 5:126 BW): 0,5% van de herbouwwaarde. */
export const VVE_MIN_PERCENT = 0.5;

export const reservePlanSchema = z.object({
  /** Saldo van het fonds op 1 januari van `balanceYear`. */
  startBalanceCents: z.number().int().min(0).max(100_000_000_000),
  balanceYear: z.number().int().min(1900).max(2200),
  /** Wat er nu werkelijk per jaar gestort wordt; leeg = onbekend. */
  currentContributionCents: z.number().int().min(0).max(100_000_000_000).nullable(),
  interestPercent: z.number().min(0).max(20),
  method: z.enum(RESERVE_METHODS),
  rebuildValueCents: z.number().int().min(0).max(1_000_000_000_000).nullable(),
});
export type ReservePlanInput = z.infer<typeof reservePlanSchema>;
export interface ReservePlanDto extends ReservePlanInput {
  buildingId: string;
  /** false = nog niet ingevuld (standaardwaarden). */
  saved: boolean;
}

export const defaultReservePlan = (buildingId: string, year: number): ReservePlanDto => ({
  buildingId,
  saved: false,
  startBalanceCents: 0,
  balanceYear: year,
  currentContributionCents: null,
  interestPercent: 0,
  method: "cost_covering",
  rebuildValueCents: null,
});

export interface ReserveYear {
  year: number;
  contributionCents: number;
  interestCents: number;
  costsCents: number;
  /** Saldo op 31 december. */
  balanceCents: number;
}

export interface ReserveComponent {
  sourceId: string;
  label: string;
  assetId: string | null;
  nlsfbCode: string | null;
  /** Eerstvolgende uitvoering vanaf het eerste jaar. */
  year: number;
  cycleYears: number;
  /** Bedrag in dat jaar (geïndexeerd). */
  costCents: number;
  /** Verstreken deel van de cyclus op 1 januari van het eerste jaar (0–1). */
  elapsed: number;
  /** Wat er nu voor dit onderdeel in het fonds hoort te zitten. */
  requiredNowCents: number;
  /** Deel van het startsaldo dat aan dit onderdeel is toegerekend. */
  allocatedCents: number;
  /** Storting per jaar tot en met de vervanging. */
  perYearCents: number;
}

export interface ReserveResult {
  method: ReserveMethod;
  fromYear: number;
  toYear: number;
  /** Storting in het eerste jaar volgens de methode. */
  perYearCents: number;
  years: ReserveYear[];
  /** Jaar met het laagste saldo. */
  criticalYear: number | null;
  lowestBalanceCents: number;
  /** Componentenmethode: onderdelen met een cyclus (bij elke methode berekend, voor de tabel). */
  components: ReserveComponent[];
  /** Som van wat er nu per onderdeel klaar hoort te liggen. */
  requiredNowCents: number;
  /** Tekort van het startsaldo ten opzichte van `requiredNowCents`. */
  backlogCents: number;
  /** Met de huidige storting: laagste saldo en het eerste jaar met een tekort. */
  current: {
    perYearCents: number;
    lowestBalanceCents: number;
    shortfallYear: number | null;
    years: ReserveYear[];
  } | null;
  /** VvE-toets: 0,5% van de herbouwwaarde, als die bekend is. */
  vveMinimumCents: number | null;
}

/** Kosten per jaar van `fromYear` tot en met `toYear`. */
function costsPerYear(rows: readonly BudgetRow[], fromYear: number, toYear: number): number[] {
  const out = Array.from({ length: toYear - fromYear + 1 }, () => 0);
  for (const r of rows) if (r.year >= fromYear && r.year <= toYear) out[r.year - fromYear]! += r.amountCents;
  return out;
}

/**
 * Saldo per jaar: saldo begin × rente (alleen over een positief saldo) + storting − kosten.
 * De storting van een jaar en de kosten van dat jaar vallen in hetzelfde jaar.
 */
function simulate(
  start: number,
  contributions: readonly number[],
  costs: readonly number[],
  rate: number,
  fromYear: number,
): ReserveYear[] {
  let balance = start;
  return costs.map((c, i) => {
    const interest = balance > 0 ? Math.round(balance * rate) : 0;
    balance += interest + contributions[i]! - c;
    return {
      year: fromYear + i,
      contributionCents: contributions[i]!,
      interestCents: interest,
      costsCents: c,
      balanceCents: balance,
    };
  });
}

const lowest = (ys: readonly ReserveYear[]) =>
  ys.reduce<ReserveYear | null>((m, y) => (!m || y.balanceCents < m.balanceCents ? y : m), null);

/** Laagste storting (hele euro's) waarbij het saldo nooit negatief wordt; `grow` = jaarlijkse groei. */
function minimalContribution(start: number, costs: readonly number[], rate: number, grow: number): number {
  const ok = (d: number) => {
    const cs = costs.map((_, i) => Math.round(d * Math.pow(1 + grow, i)));
    return simulate(start, cs, costs, rate, 0).every((y) => y.balanceCents >= 0);
  };
  if (ok(0)) return 0;
  // Zoeken in hele euro's; elk jaar alle kosten storten is altijd genoeg.
  let lo = 1;
  let hi = Math.max(1, Math.ceil(costs.reduce((s, c) => s + c, 0) / 100));
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (ok(mid * 100)) hi = mid;
    else lo = mid + 1;
  }
  return lo * 100;
}

/** Storting per jaar om `need` op te bouwen in `n` stortingen met rente `rate` (annuïteit). */
function annuity(need: number, n: number, rate: number): number {
  if (need <= 0) return 0;
  if (rate <= 0) return need / n;
  return (need * rate) / (Math.pow(1 + rate, n) - 1);
}

/**
 * Onderdelen voor de componentenmethode: vervangingen met een cyclus van 2 jaar of meer, met hun eerstvolgende uitvoering
 * vanaf `fromYear`. Het verstreken deel van de cyclus moet nu al in het fonds zitten.
 */
export function reserveComponents(
  rows: readonly BudgetRow[],
  fromYear: number,
): Omit<ReserveComponent, "allocatedCents" | "perYearCents">[] {
  const first = new Map<string, BudgetRow>();
  for (const r of rows) {
    if (r.workType !== "replacement" || r.source !== "item" || !r.cycleYears || r.cycleYears < 2 || r.year < fromYear)
      continue;
    const cur = first.get(r.sourceId);
    if (!cur || r.year < cur.year) first.set(r.sourceId, r);
  }
  return [...first.values()]
    .map((r) => {
      const cycle = r.cycleYears!;
      const remaining = r.year - fromYear;
      const elapsed = Math.min(1, Math.max(0, (cycle - remaining) / cycle));
      return {
        sourceId: r.sourceId,
        label: r.label,
        assetId: r.assetId,
        nlsfbCode: r.nlsfbCode,
        year: r.year,
        cycleYears: cycle,
        costCents: r.amountCents,
        elapsed,
        requiredNowCents: Math.round(r.amountCents * elapsed),
      };
    })
    .sort((a, b) => a.year - b.year || a.label.localeCompare(b.label, "nl"));
}

/**
 * Reservering van één gebouw over `fromYear`–`toYear` (de reserveringshorizon).
 * - kostendekkend: laagste vaste storting zonder tekort, met startsaldo en rente;
 * - meegroeiend: idem, maar de storting stijgt elk jaar met de index;
 * - componenten: per onderdeel met een cyclus (bedrag − toegerekend startsaldo) verdeeld over de jaren
 *   tot en met de vervanging, daarna bedrag ÷ cyclus voor de volgende keer; eenmalige vervangingen
 *   kostendekkend uit de rest van het startsaldo.
 * Alleen vervangingen tellen mee; de andere rijen worden genegeerd.
 */
export function reservePlan(
  allRows: readonly BudgetRow[],
  plan: ReservePlanInput,
  indexPercent: number,
  fromYear: number,
  toYear: number,
): ReserveResult {
  const rows = allRows.filter((r) => r.workType === "replacement");
  const rate = plan.interestPercent / 100;
  const grow = indexPercent / 100;
  const costs = costsPerYear(rows, fromYear, toYear);
  const start = plan.startBalanceCents;
  const base = reserveComponents(rows, fromYear);
  const requiredNow = base.reduce((s, c) => s + c.requiredNowCents, 0);
  // Startsaldo eerst naar de onderdelen, naar rato van wat er nu klaar hoort te liggen.
  const share = requiredNow > 0 ? Math.min(1, start / requiredNow) : 0;
  const components: ReserveComponent[] = base.map((c) => {
    const allocated = Math.round(c.requiredNowCents * share);
    const n = c.year - fromYear + 1;
    const growAtStart = allocated * Math.pow(1 + rate, n - 1);
    return {
      ...c,
      allocatedCents: allocated,
      perYearCents: Math.ceil(annuity(c.costCents - growAtStart, n, rate) / 100) * 100,
    };
  });

  let contributions: number[];
  if (plan.method === "components") {
    const ids = new Set(components.map((c) => c.sourceId));
    const other = rows.filter((r) => !(r.source === "item" && ids.has(r.sourceId)));
    const rest = Math.max(0, start - components.reduce((s, c) => s + c.allocatedCents, 0));
    const running = minimalContribution(rest, costsPerYear(other, fromYear, toYear), rate, 0);
    contributions = costs.map((_, i) => {
      const year = fromYear + i;
      let d = running;
      for (const c of components) {
        // Tot en met de vervanging inhalen; daarna sparen voor de volgende keer.
        d +=
          year <= c.year
            ? c.perYearCents
            : Math.ceil((c.costCents * Math.pow(1 + grow, c.cycleYears)) / c.cycleYears / 100) * 100;
      }
      return d;
    });
  } else {
    const g = plan.method === "cost_covering_indexed" ? grow : 0;
    const d = minimalContribution(start, costs, rate, g);
    contributions = costs.map((_, i) => Math.round(d * Math.pow(1 + g, i)));
  }

  const years = simulate(start, contributions, costs, rate, fromYear);
  const low = lowest(years);
  let current: ReserveResult["current"] = null;
  if (plan.currentContributionCents != null) {
    const ys = simulate(
      start,
      costs.map(() => plan.currentContributionCents!),
      costs,
      rate,
      fromYear,
    );
    current = {
      perYearCents: plan.currentContributionCents,
      lowestBalanceCents: lowest(ys)?.balanceCents ?? start,
      shortfallYear: ys.find((y) => y.balanceCents < 0)?.year ?? null,
      years: ys,
    };
  }
  return {
    method: plan.method,
    fromYear,
    toYear,
    perYearCents: contributions[0] ?? 0,
    years,
    criticalYear: low && costs.some((c) => c > 0) ? low.year : null,
    lowestBalanceCents: low?.balanceCents ?? start,
    components,
    requiredNowCents: requiredNow,
    backlogCents: Math.max(0, requiredNow - start),
    current,
    vveMinimumCents:
      plan.rebuildValueCents != null ? Math.round((plan.rebuildValueCents * VVE_MIN_PERCENT) / 100) : null,
  };
}

/** Reservering per gebouw en opgeteld (dashboard, PDF, Excel). Gebouwen zonder plan krijgen de standaard. */
export function reserveByBuilding(
  rows: readonly BudgetRow[],
  plans: readonly ReservePlanDto[],
  buildingIds: readonly string[],
  indexPercent: number,
  fromYear: number,
  toYear: number,
): { perYearCents: number; buildings: (ReserveResult & { buildingId: string; plan: ReservePlanDto })[] } {
  const byBuilding = new Map<string, BudgetRow[]>();
  for (const r of rows) byBuilding.set(r.buildingId, [...(byBuilding.get(r.buildingId) ?? []), r]);
  const out = buildingIds.map((id) => {
    const plan = plans.find((p) => p.buildingId === id) ?? defaultReservePlan(id, fromYear);
    return { buildingId: id, plan, ...reservePlan(byBuilding.get(id) ?? [], plan, indexPercent, fromYear, toYear) };
  });
  return { perYearCents: out.reduce((s, b) => s + b.perYearCents, 0), buildings: out };
}

/** Korte uitleg bij het kerncijfer, gelijk in dashboard, PDF en Excel. */
export function reserveSummaryText(r: ReturnType<typeof reserveByBuilding>, fromYear: number, toYear: number): string {
  const one = r.buildings.length === 1 ? r.buildings[0]! : null;
  const span = `vervangingen ${fromYear}–${toYear}`;
  if (!one) return `${span}; per gebouw een eigen fonds, opgeteld`;
  const start = one.plan.startBalanceCents ? "met startsaldo" : "vanaf een leeg fonds";
  const crit = one.criticalYear != null && one.method !== "components" ? `; kritisch jaar ${one.criticalYear}` : "";
  return `${RESERVE_METHOD_LABELS[one.method].toLowerCase()}, ${span}, ${start}${crit}`;
}
